import React, { Suspense } from "react";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";

// ✅ CommunityBackground : gère default OU named export
const CommunityBackground = React.lazy(() =>
  import("@/components/posts/CommunityBackground").then((m) => ({
    default: (m as any).default ?? (m as any).CommunityBackground,
  }))
);

// ✅ Lazy robustes : prennent default OU named (HeroSection, etc.)
const HeroSection = React.lazy(() =>
  import("@/components/about/HeroSection").then((m) => ({
    default: (m as any).default ?? (m as any).HeroSection,
  }))
);
const OurMissionVision = React.lazy(() =>
  import("@/components/about/OurMissionVision").then((m) => ({
    default: (m as any).default ?? (m as any).OurMissionVision,
  }))
);
const CoreValues = React.lazy(() =>
  import("@/components/about/CoreValues").then((m) => ({
    default: (m as any).default ?? (m as any).CoreValues,
  }))
);
const OurJourney = React.lazy(() =>
  import("@/components/about/OurJourney").then((m) => ({
    default: (m as any).default ?? (m as any).OurJourney,
  }))
);
const LeadershipTeam = React.lazy(() =>
  import("@/components/about/LeadershipTeam").then((m) => ({
    default: (m as any).default ?? (m as any).LeadershipTeam,
  }))
);
const CoreTechnologies = React.lazy(() =>
  import("@/components/about/CoreTechnologies").then((m) => ({
    default: (m as any).default ?? (m as any).CoreTechnologies,
  }))
);
const ConnectWithUs = React.lazy(() =>
  import("@/components/about/ConnectWithUs").then((m) => ({
    default: (m as any).default ?? (m as any).ConnectWithUs,
  }))
);

// Loading + Error UI
const LoadingFallback = () => (
  <div className="flex justify-center items-center py-12">
    <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
  </div>
);

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean; err?: any }> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(err: any) {
    return { hasError: true, err };
  }
  componentDidCatch(err: any) {
    console.error("About page error:", err);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="max-w-3xl mx-auto my-12 p-6 rounded-xl bg-red-950/30 border border-red-800 text-red-200">
          <h2 className="text-lg font-semibold mb-2">Something went wrong loading the About page.</h2>
          <p className="text-sm opacity-80">Check component exports (default vs named) and network errors in the console.</p>
        </div>
      );
    }
    return this.props.children as any;
  }
}

const AboutUs = () => {
  return (
    <div className="relative">
      <Helmet>
        <title>About Digika.ai | AI-Powered Freelance Platform</title>
        <meta
          name="description"
          content="Learn about Digika.ai - The AI that connects talent and companies for innovative, high-performance & gaming through blockchain technology."
        />
      </Helmet>

      <ErrorBoundary>
        <Suspense fallback={<LoadingFallback />}>
          <CommunityBackground />
        </Suspense>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 relative z-10">
          <Suspense fallback={<LoadingFallback />}>
            <HeroSection />
          </Suspense>

          {[OurMissionVision, CoreValues, OurJourney, LeadershipTeam, CoreTechnologies, ConnectWithUs].map(
            (Section, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5, delay: 0.1 * (i + 1) }}
                className="mt-8"
              >
                <Suspense fallback={<LoadingFallback />}>
                  <Section />
                </Suspense>
              </motion.div>
            )
          )}
        </div>
      </ErrorBoundary>
    </div>
  );
};

export default AboutUs;
